import type { ReactNode } from 'react';
import type { ISelectedOrganization, IService, SelectedOrganizations } from './ServiceProviderAddEdit';
import { fetchJson } from '../../../store/fetch';
import { translateKey } from '../../../translate';
import {
    AdminTable,
    Button,
    DeleteButton,
    Dropdown,
    FormGroup,
    FormHeader,
    FormLabel,
    InputGroup,
    TableBody,
    TableData,
    TableHead,
    TableHeader,
    TableRow,
    UserTextParagraph,
} from '../../elements/common';
import { Component } from '../../elements/Component';

interface IServiceOrganizationConsumerListAddEditProps {
    onChange: (selectedOrganizations: SelectedOrganizations) => Promise<void>;
    selectedOrganizations: SelectedOrganizations;
    service: IService;
}

interface IServiceOrganizationConsumerListAddEditState {
    organizations: ISelectedOrganization[];
    selectedOrganizationId: string | null;
}

export class ServiceOrganizationConsumerListAddEdit extends Component<
    IServiceOrganizationConsumerListAddEditProps,
    IServiceOrganizationConsumerListAddEditState
> {
    constructor(props: IServiceOrganizationConsumerListAddEditProps) {
        super(props);

        this.state = {
            organizations: [],
            selectedOrganizationId: null,
        };
    }

    public async componentDidMount() {
        await this.loadServiceConsumerOrganizations(this.props.service.id);
    }

    public async componentDidUpdate(prevProps: Readonly<IServiceOrganizationConsumerListAddEditProps>) {
        if (this.props.service.id !== prevProps.service.id) {
            await this.loadServiceConsumerOrganizations(this.props.service.id);
        }
    }

    public render() {
        const rows: ReactNode[] = [];

        this.props.selectedOrganizations.valueSeq().forEach((selectedOrganization) => {
            if (!selectedOrganization) {
                return;
            }
            rows.push(
                <TableRow key={selectedOrganization.id}>
                    <TableData>{`${selectedOrganization.name} ${
                        !selectedOrganization.isActive ? `(${translateKey('common.isNotActive')})` : ''
                    } `}</TableData>
                    <TableData align="right">
                        <DeleteButton onClick={async () => await this.removeServiceConsumer(selectedOrganization.id)} />
                    </TableData>
                </TableRow>
            );
        });

        return (
            <>
                <FormHeader>{translateKey('serviceConsumer.consumers')}</FormHeader>

                {rows.length === 0 ? (
                    <UserTextParagraph>{translateKey('profile.noConsumer', this.props.service.name)}</UserTextParagraph>
                ) : (
                    <AdminTable>
                        <TableHead>
                            <TableRow>
                                <TableHeader>{translateKey('organization.organization')} </TableHeader>
                                <TableHeader></TableHeader>
                            </TableRow>
                        </TableHead>
                        <TableBody>{rows}</TableBody>
                    </AdminTable>
                )}

                <FormGroup>
                    <FormLabel>
                        {translateKey('common.add')} {translateKey('organization.organization')}
                    </FormLabel>
                    <InputGroup>
                        <Dropdown
                            onChange={async (value) => await this.setState({ selectedOrganizationId: value })}
                            placeholder={translateKey('common.selectItem', translateKey('organization.organization'))}
                            selectOptions={this.getOrganizationSelectOptions()}
                            value={this.state.selectedOrganizationId}
                        />
                        <Button
                            color="secondary"
                            disabled={!this.state.selectedOrganizationId}
                            onClick={async () => await this.addServiceConsumer(this.state.selectedOrganizationId)}
                        >
                            {translateKey('common.add')}
                        </Button>
                    </InputGroup>
                </FormGroup>
            </>
        );
    }

    private async removeServiceConsumer(selectedOrganizationId: string) {
        await this.props.onChange(this.props.selectedOrganizations.delete(selectedOrganizationId));
    }

    private async addServiceConsumer(selectedOrganizationId: string | null) {
        if (!selectedOrganizationId) {
            return;
        }
        const selectedServiceOrganization = await this.state.organizations.find(
            (item) => item.id === selectedOrganizationId
        );
        if (selectedServiceOrganization) {
            await this.setState({ selectedOrganizationId: null });
            await this.props.onChange(
                this.props.selectedOrganizations.set(selectedOrganizationId, selectedServiceOrganization)
            );
        }
    }

    private getOrganizationSelectOptions() {
        let options: { text: string; value: string }[] = [];
        for (const item of this.state.organizations) {
            if (!this.props.selectedOrganizations.has(item.id)) {
                options = options.concat({ value: item.id, text: item.name });
            }
        }

        return options;
    }

    private async loadServiceConsumerOrganizations(serviceId: string) {
        const url = `/api/serviceConsumersList/${serviceId}/organizations?isActive=true`;
        const results = await fetchJson(url, 'GET');
        await this.setState({ organizations: results.json.items });
    }
}
